import React, { useState, useEffect, useCallback } from 'react';
import {
  Table, Form, Input, Select, Button, Space, Popconfirm, message, Row, Card, List, Tag,
} from 'antd';
import DraggableModal from '../../components/DraggableModal';
import { PlusOutlined, DownOutlined, RightOutlined, ArrowLeftOutlined } from '@ant-design/icons';
import api from '../../api';
import { getStoredUser, isSuperAdmin } from '../../userAuth';

const SPECIAL_LAST_LOCATION_NAME = '3办公楼库房大连港专门办案场所边检处606室办公室6楼小木屋';
const compareLocationNameAsc = (a, b) => {
  const left = String(a || '').trim();
  const right = String(b || '').trim();
  const leftSpecial = left === SPECIAL_LAST_LOCATION_NAME;
  const rightSpecial = right === SPECIAL_LAST_LOCATION_NAME;
  if (leftSpecial && !rightSpecial) return 1;
  if (!leftSpecial && rightSpecial) return -1;
  const leftParts = left.split(/(\d+)/).filter(Boolean);
  const rightParts = right.split(/(\d+)/).filter(Boolean);
  const len = Math.max(leftParts.length, rightParts.length);
  for (let i = 0; i < len; i += 1) {
    const lp = leftParts[i];
    const rp = rightParts[i];
    if (lp === undefined) return -1;
    if (rp === undefined) return 1;
    const lNum = /^\d+$/.test(lp);
    const rNum = /^\d+$/.test(rp);
    if (lNum && rNum) {
      const diff = Number(lp) - Number(rp);
      if (diff !== 0) return diff;
      continue;
    }
    if (lNum && !rNum) return -1;
    if (!lNum && rNum) return 1;
    const cmp = String(lp).localeCompare(String(rp), 'zh-Hans-CN', { sensitivity: 'base' });
    if (cmp !== 0) return cmp;
  }
  return 0;
};

export default function Regions() {
  const user = getStoredUser();
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form] = Form.useForm();
  const [deleteFilterForm] = Form.useForm();
  const [deleteFilterModalOpen, setDeleteFilterModalOpen] = useState(false);
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [searchName, setSearchName] = useState('');
  const [locations, setLocations] = useState([]);
  const [users, setUsers] = useState([]);
  const [permissionCategories, setPermissionCategories] = useState([]);
  const [activeView, setActiveView] = useState('manage');
  const [detailRows, setDetailRows] = useState([]);
  const [detailLoading, setDetailLoading] = useState(false);
  const [detailOpen, setDetailOpen] = useState(false);
  const [detailTitle, setDetailTitle] = useState('');
  const [detailRegions, setDetailRegions] = useState([]);
  const [showUnselected, setShowUnselected] = useState(false);
  const [regionOptions, setRegionOptions] = useState([]);
  const [assignOpen, setAssignOpen] = useState(false);
  const [assignSaving, setAssignSaving] = useState(false);
  const [assignTargetName, setAssignTargetName] = useState('');
  const [assignRegionIds, setAssignRegionIds] = useState([]);

  const buildLocationUsageMap = useCallback((excludeRegionId) => {
    const usage = {};
    (Array.isArray(data) ? data : []).forEach((region) => {
      if (excludeRegionId && region.id === excludeRegionId) return;
      const locIds = Array.isArray(region.location_ids) ? region.location_ids : [];
      locIds.forEach((lid) => {
        if (!usage[lid]) usage[lid] = [];
        usage[lid].push(region.name);
      });
    });
    return usage;
  }, [data]);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get('/regions');
      const all = Array.isArray(res) ? res : [];
      const kw = (searchName || '').trim();
      const rows = kw ? all.filter(r => String(r.name || '').includes(kw)) : all;
      setData(rows);
      setSelectedRowKeys([]);
    } catch { message.error('获取区域列表失败'); }
    setLoading(false);
  }, [searchName]);

  const fetchOptions = useCallback(async () => {
    try {
      const [locRes, permRes, regionRes] = await Promise.all([
        api.get('/asset-locations'),
        api.get('/permission-categories'),
        api.get('/regions'),
      ]);
      setLocations(Array.isArray(locRes) ? locRes : []);
      // users：后端约束 page_size <= 100，这里分页拉取
      const pageSize = 100;
      let page = 1;
      const allUsers = [];
      // eslint-disable-next-line no-constant-condition
      while (true) {
        // eslint-disable-next-line no-await-in-loop
        const ur = await api.get('/users', { params: { page, page_size: pageSize } });
        const items = Array.isArray(ur?.items) ? ur.items : [];
        allUsers.push(...items);
        const total = Number(ur?.total || 0);
        if (!total || allUsers.length >= total || !items.length) break;
        page += 1;
      }
      setUsers(allUsers);

      // permission-categories：兼容返回数组或 {items: []}
      const permItems = Array.isArray(permRes)
        ? permRes
        : (Array.isArray(permRes?.items) ? permRes.items : []);
      setPermissionCategories(permItems);
      setRegionOptions((Array.isArray(regionRes) ? regionRes : []).map(r => ({ value: r.id, label: r.name })));
    } catch {
      // ignore
    }
  }, []);

  useEffect(() => { fetchData(); fetchOptions(); }, [fetchData, fetchOptions]);
  const fetchDetailRows = useCallback(async () => {
    setDetailLoading(true);
    try {
      const res = await api.get('/regions/location-usage-by-name');
      const list = Array.isArray(res?.all) ? res.all : [];
      const sorted = [...list].sort((a, b) => compareLocationNameAsc(a.location_name, b.location_name));
      setDetailRows(sorted);
    } catch (err) {
      message.error(err.message || '获取区域详情失败');
    } finally {
      setDetailLoading(false);
    }
  }, []);
  useEffect(() => {
    if (activeView !== 'details') return;
    fetchDetailRows();
  }, [activeView, fetchDetailRows]);

  if (!isSuperAdmin(user)) return <div style={{ padding: 40, textAlign: 'center', fontSize: 18 }}>无权限</div>;

  const handleCreate = () => { setEditing(null); form.resetFields(); setModalOpen(true); };

  const handleEdit = (record) => {
    setEditing(record);
    form.setFieldsValue({
      name: record.name,
      location_ids: record.location_ids || [],
      manager_user_ids: record.manager_user_ids || [],
      permission_category_id: record.permission_category_id,
    });
    setModalOpen(true);
  };

  const locationUsageMap = buildLocationUsageMap(editing?.id);
  const locationOptions = locations.map((l) => {
    const usedBy = locationUsageMap[l.id] || [];
    const suffix = usedBy.length ? `（已被选${usedBy.length}，${usedBy.join('、')}）` : '';
    return {
      value: l.id,
      label: `${l.location_name}${suffix}`,
      rawName: l.location_name,
    };
  });
  const userOptions = users.map(u => ({
    value: u.id,
    label: `${u.username}（${u.work_no}）`,
  }));
  const permissionOptions = permissionCategories.map(c => ({
    value: c.id,
    label: c.name,
  }));

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();
      if (editing) {
        await api.put(`/regions/${editing.id}`, values);
        message.success('更新成功');
      } else {
        await api.post('/regions', values);
        message.success('创建成功');
      }
      setModalOpen(false);
      fetchData();
    } catch { /* validation or api error */ }
  };

  const handleDelete = async (id) => {
    try {
      const res = await api.post('/regions/delete-batch', { scope: 'ids', ids: [id] });
      message.success(`删除成功（${res?.deleted_count || 0} 条）`);
      fetchData();
    } catch (err) {
      const skipped = Array.isArray(err?.data?.skipped) ? err.data.skipped : [];
      if (skipped.length) {
        const reasons = [...new Set(skipped.map(s => s?.reason).filter(Boolean))];
        message.warning(reasons.length ? reasons.join('；') : (err.message || '删除失败'));
        return;
      }
      message.error(err.message || '删除失败');
    }
  };

  const handleDeleteSelected = async () => {
    if (!selectedRowKeys.length) {
      message.warning('请先勾选要删除的区域');
      return;
    }
    try {
      const res = await api.post('/regions/delete-batch', { scope: 'ids', ids: selectedRowKeys });
      message.success(`批量删除成功（${res?.deleted_count || 0} 条）`);
      fetchData();
    } catch (err) {
      const skipped = Array.isArray(err?.data?.skipped) ? err.data.skipped : [];
      if (skipped.length) {
        const reasons = [...new Set(skipped.map(s => s?.reason).filter(Boolean))];
        message.warning(reasons.length ? reasons.join('；') : (err.message || '删除失败'));
        return;
      }
      message.error(err.message || '删除失败');
    }
  };

  const handleDeleteByFilter = async () => {
    try {
      const values = await deleteFilterForm.validateFields();
      const hasAny = [values.keyword].some(v => v !== undefined && v !== null && v !== '');
      if (!hasAny) {
        message.warning('请至少填写一项筛选条件，禁止空白删除');
        return;
      }
      const payload = { scope: 'filtered' };
      if (values.keyword) payload.keyword = values.keyword;
      const res = await api.post('/regions/delete-batch', payload);
      message.success(`按筛选条件删除成功（${res?.deleted_count || 0} 条）`);
      setDeleteFilterModalOpen(false);
      fetchData();
    } catch (err) {
      const skipped = Array.isArray(err?.data?.skipped) ? err.data.skipped : [];
      if (skipped.length) {
        const reasons = [...new Set(skipped.map(s => s?.reason).filter(Boolean))];
        message.warning(reasons.length ? reasons.join('；') : (err.message || '删除失败'));
        return;
      }
      message.error(err.message || '删除失败');
    }
  };

  const columns = [
    { title: '区域名称', dataIndex: 'name', key: 'name' },
    {
      title: '区域范围(地点)',
      dataIndex: 'location_names',
      key: 'location_names',
      render: (v) => (v || []).join('、') || '-',
    },
    {
      title: '负责人',
      dataIndex: 'manager_names',
      key: 'manager_names',
      width: 220,
      ellipsis: true,
      render: (v) => (v || []).join('、') || '-',
    },
    {
      title: '操作', key: 'actions',
      render: (_, record) => (
        <Space>
          <Button type="link" size="small" onClick={() => handleEdit(record)}>编辑</Button>
          <Popconfirm title="确认删除?" onConfirm={() => handleDelete(record.id)}>
            <Button type="link" size="small" danger>删除</Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  const openDetail = async (record) => {
    if (!record?.location_name || Number(record.selected_count || 0) <= 0) return;
    setDetailOpen(true);
    setDetailTitle(record.location_name || '');
    try {
      const res = await api.post('/regions/location-usage/by-name/regions', { location_name: record.location_name });
      setDetailRegions(Array.isArray(res?.regions) ? res.regions : []);
    } catch (err) {
      message.error(err.message || '获取区域明细失败');
      setDetailRegions([]);
    }
  };
  const openAssign = async (record) => {
    const name = record?.location_name || '';
    if (!name) return;
    setAssignTargetName(name);
    setAssignOpen(true);
    setAssignSaving(true);
    try {
      const res = await api.post('/regions/location-usage/by-name/regions', { location_name: name });
      const rs = Array.isArray(res?.regions) ? res.regions : [];
      setAssignRegionIds(rs.map(x => x.id));
    } catch {
      setAssignRegionIds([]);
    } finally {
      setAssignSaving(false);
    }
  };
  const saveAssign = async () => {
    if (!assignTargetName) return;
    setAssignSaving(true);
    try {
      await api.post('/regions/location-usage/by-name/assign', {
        location_name: assignTargetName,
        region_ids: assignRegionIds,
      });
      message.success('分配已保存');
      setAssignOpen(false);
      await fetchDetailRows();
      await fetchData();
    } catch (err) {
      message.error(err.message || '保存分配失败');
    } finally {
      setAssignSaving(false);
    }
  };
  const detailColumns = [
    { title: '存放地点', dataIndex: 'location_name', key: 'location_name' },
    {
      title: '被选次数',
      dataIndex: 'selected_count',
      key: 'selected_count',
      width: 120,
      render: (v, record) => {
        const n = Number(v || 0);
        if (n <= 0) return 0;
        return <Button type="link" size="small" onClick={() => openDetail(record)}>{n}</Button>;
      },
    },
    {
      title: '分配',
      key: 'assign',
      width: 130,
      render: (_, record) => (
        <Button type="link" size="small" onClick={() => openAssign(record)}>
          {Number(record?.selected_count || 0) > 0 ? '编辑分配' : '分配'}
        </Button>
      ),
    },
  ];
  const unselectedRows = detailRows.filter(r => Number(r.selected_count || 0) === 0);
  const selectedRows = detailRows.filter(r => Number(r.selected_count || 0) > 0);

  return (
    <div style={{ padding: 24 }}>
      {activeView === 'details' ? (
        <>
          <div style={{ marginBottom: 16 }}>
            <Button icon={<ArrowLeftOutlined />} onClick={() => setActiveView('manage')}>返回</Button>
          </div>
          <Card
            style={{ marginBottom: 16 }}
            title={(
              <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
                <Button
                  type="text"
                  size="small"
                  icon={showUnselected ? <DownOutlined /> : <RightOutlined />}
                  onClick={() => setShowUnselected(!showUnselected)}
                />
                <span>{`未被选（${unselectedRows.length}）`}</span>
              </span>
            )}
          >
            {showUnselected ? (
            <Table
              rowKey="location_name"
              columns={detailColumns}
              dataSource={unselectedRows}
              loading={detailLoading}
              pagination={false}
              locale={{ emptyText: '全部存放地点都已被区域选择' }}
            />
            ) : null}
          </Card>
          <Card title={`被选（${selectedRows.length}）`}>
            <Table
              rowKey="location_name"
              columns={detailColumns}
              dataSource={selectedRows}
              loading={detailLoading}
              pagination={{ pageSize: 20, showTotal: t => `共 ${t} 条` }}
              locale={{ emptyText: '暂无被选中的存放地点' }}
            />
          </Card>
          <DraggableModal
            title={`区域明细 - ${detailTitle || '存放地点'}`}
            open={detailOpen}
            onCancel={() => setDetailOpen(false)}
            footer={null}
            destroyOnClose
          >
            <List
              dataSource={detailRegions}
              locale={{ emptyText: '该存放地点当前未被任何区域选中' }}
              renderItem={(item) => (
                <List.Item>
                  <Tag color="blue">{item.name}</Tag>
                </List.Item>
              )}
            />
          </DraggableModal>
          <DraggableModal
            title={`分配区域 - ${assignTargetName || ''}`}
            open={assignOpen}
            confirmLoading={assignSaving}
            onOk={saveAssign}
            onCancel={() => setAssignOpen(false)}
            destroyOnClose
          >
            <Select
              mode="multiple"
              style={{ width: '100%' }}
              placeholder="请选择区域"
              value={assignRegionIds}
              onChange={setAssignRegionIds}
              options={regionOptions}
              optionFilterProp="label"
              showSearch
            />
          </DraggableModal>
        </>
      ) : (
        <>
      <Row justify="space-between" align="middle" style={{ marginBottom: 16 }}>
        <Space>
          <h2 style={{ margin: 0 }}>区域管理</h2>
        </Space>
        <Space>
          <Input
            placeholder="搜索区域名称"
            value={searchName}
            onChange={(e) => setSearchName(e.target.value)}
            onPressEnter={fetchData}
            allowClear
            style={{ width: 220 }}
          />
          <Button onClick={fetchData}>搜索</Button>
          <Popconfirm title={`仅删除已勾选项：确认删除 ${selectedRowKeys.length} 个区域？`} onConfirm={handleDeleteSelected} disabled={!selectedRowKeys.length}>
            <Button danger disabled={!selectedRowKeys.length}>删除已勾选</Button>
          </Popconfirm>
          <Button danger onClick={() => { deleteFilterForm.resetFields(); setDeleteFilterModalOpen(true); }}>按筛选条件删除</Button>
          <Button type="primary" icon={<PlusOutlined />} onClick={handleCreate}>新建区域</Button>
          <Button onClick={() => setActiveView('details')}>区域详情</Button>
        </Space>
      </Row>

      <Table
        rowKey="id"
        rowSelection={{ selectedRowKeys, onChange: setSelectedRowKeys }}
        columns={columns}
        dataSource={data}
        loading={loading}
        pagination={{ showTotal: t => `共 ${t} 条` }}
      />

      <DraggableModal
        title="按筛选条件删除区域（需重新填写）"
        open={deleteFilterModalOpen}
        onOk={handleDeleteByFilter}
        onCancel={() => setDeleteFilterModalOpen(false)}
        okText="确认删除"
        cancelText="取消"
        destroyOnClose
      >
        <div style={{ marginBottom: 12, color: '#cf1322' }}>
          为避免误删，必须重新填写筛选条件；禁止空白条件删除。
        </div>
        <Form form={deleteFilterForm} layout="vertical">
          <Form.Item name="keyword" label="区域名称关键词">
            <Input allowClear />
          </Form.Item>
        </Form>
      </DraggableModal>

      <DraggableModal
        title={editing ? '编辑区域' : '新建区域'}
        open={modalOpen}
        onOk={handleSubmit}
        onCancel={() => setModalOpen(false)}
        destroyOnClose
      >
        <Form form={form} layout="vertical">
          <Form.Item name="name" label="区域名称" rules={[{ required: true, message: '请输入区域名称' }]}>
            <Input />
          </Form.Item>
          <Form.Item name="location_ids" label="区域范围（存放地点）" rules={[{ required: true, message: '请选择至少一个地点' }]}>
            <Select
              mode="multiple"
              showSearch
              optionFilterProp="label"
              options={locationOptions}
              placeholder="请选择地点"
            />
          </Form.Item>
          <Form.Item name="manager_user_ids" label="区域负责人" rules={[{ required: true, message: '请选择负责人' }]}>
            <Select
              mode="multiple"
              showSearch
              optionFilterProp="label"
              options={userOptions}
              placeholder="请选择负责人"
            />
          </Form.Item>
          <Form.Item name="permission_category_id" label="角色类别" rules={[{ required: true, message: '请选择角色类别' }]}>
            <Select
              showSearch
              optionFilterProp="label"
              options={permissionOptions}
              placeholder="请选择角色类别"
            />
          </Form.Item>
        </Form>
      </DraggableModal>
        </>
      )}
    </div>
  );
}
